import { initialsFormatter } from '@asn/formatters';

import { Component, OnDestroy, OnInit, input, inject } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { ApFormControlPipe } from '@ap/cdk';
import { ApContentService, ApKeyOverridePipe, ApContentPipe, ApContentComponent } from '@ap/content';
import { ApDiacriticsNormalizeFormValueDirective } from '@ap/diacritics-normalize';
import { ErrorMessages } from '@ap/form/errors';
import { ApInputComponent } from '@ap/input';
import { ApInitialsInputComponent } from '@ap/input/initials';
import { ApNotificationComponent } from '@ap/notification';
import { ApRadioGroupComponent, ApRadioButtonComponent } from '@ap/radio';

import { FieldLengths } from './personal-details.expressions';
import content from './personal-details.content.json';
import { PersonalDetailsForm } from './personal-details.model';

/** This component can be used by customers to enter details about - name (initials, insertion lastname - age (date of birth) */
@Component({
    selector: 'ap-personal-details',
    templateUrl: './personal-details.component.html',
    styleUrls: ['./personal-details.component.scss'],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        ApRadioGroupComponent,
        ApRadioButtonComponent,
        ApInputComponent,
        ApDiacriticsNormalizeFormValueDirective,
        ApInitialsInputComponent,
        ApNotificationComponent,
        ApContentComponent,
        ApContentPipe,
        ApKeyOverridePipe,
        ApFormControlPipe
    ]
})
export class ApPersonalDetailsComponent implements OnInit, OnDestroy {
    private readonly contentService = inject(ApContentService);

    /**
     * Required - The form group to bind to see {@link ApPersonalDetailsService}
     */
    public readonly personalDetailsForm = input.required<FormGroup<PersonalDetailsForm>>();

    /**
     * keymap for content overrides
     */
    public readonly contentKeyMap = input<{
        [contentKey: string]: string;
    }>();

    protected genderErrorMessages: ErrorMessages;
    protected firstNamesErrorMessages: ErrorMessages;
    protected givenNameErrorMessages: ErrorMessages;
    protected initialsErrorMessages: ErrorMessages;
    protected insertionErrorMessages: ErrorMessages;
    protected lastNameErrorMessages: ErrorMessages;

    protected genderOptions = [
        { value: 'M', content: 'personal.details.gender.M' },
        { value: 'F', content: 'personal.details.gender.F', last: true }
    ];

    private diacriticFieldValue: any = {
        lastName: false,
        firstNames: false,
        givenName: false
    };

    private readonly destroy$ = new Subject<void>();

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.setErrorMessages();

        this.personalDetailsForm()
            .get('firstNames')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value: string) => this.setInitials(value));
        this.setInitials(this.personalDetailsForm().get('firstNames').value);
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected getErrorMessage(field: string, error: string): string {
        const initialKey = `personal.details.${field}.error.${error}`;
        const key = new ApKeyOverridePipe().transform(initialKey, this.contentKeyMap());
        return this.contentService.get(key);
    }

    protected setInitials(value: string): void {
        if (value) {
            const firstnames: string[] = this.personalDetailsForm().get('firstNames').value.split(' ');
            const initials = firstnames.map((name) => name[0]).join('');

            this.personalDetailsForm().get('initials').setValue(initialsFormatter.format(initials));
        }
    }

    protected diacriticsChange(diacriticValue: boolean, fieldName: string): void {
        this.diacriticFieldValue[fieldName] = diacriticValue;
    }

    protected hasDiacritics(): boolean {
        const diacriticFields = ['lastName', 'firstNames', 'givenName'];

        return diacriticFields.some((field: string) => this.diacriticFieldValue[field]);
    }

    protected personalNameHasDiacritics(): boolean {
        const diacriticFields = ['firstNames', 'givenName'];

        return diacriticFields.some((field: string) => this.diacriticFieldValue[field]);
    }

    protected getPersonalName(): string {
        const firstNames = this.personalDetailsForm().get('firstNames').value;
        const givenName = this.personalDetailsForm().get('givenName').value;
        if (!firstNames && !givenName) {
            return null;
        }
        return givenName ? `${firstNames} (${givenName})` : firstNames;
    }

    protected lastNameHasDiacritics(): boolean {
        return this.diacriticFieldValue.lastName;
    }

    protected getLastName(): string {
        return this.personalDetailsForm().get('lastName').value;
    }

    private setErrorMessages(): void {
        this.genderErrorMessages = {
            required: this.contentService.get('personal.details.gender.error.required')
        };
        this.firstNamesErrorMessages = {
            required: this.contentService.get('personal.details.firstNames.error.required'),
            mismatch: this.contentService.get('personal.details.firstNames.error.mismatch'),
            incorrect: this.contentService.get('personal.details.firstNames.error.incorrect'),
            minlength: this.contentService.get('personal.details.firstNames.error.minLength')
        };
        this.givenNameErrorMessages = {
            incorrect: this.contentService.get('personal.details.givenName.error.incorrect')
        };
        this.initialsErrorMessages = {
            required: this.contentService.get('personal.details.initials.error.required'),
            incorrect: this.contentService.get('personal.details.initials.error.incorrect')
        };
        this.insertionErrorMessages = {
            incorrect: this.contentService.get('personal.details.insertion.error.incorrect'),
            maxlength: this.contentService.get('personal.details.insertion.error.maxlength', {
                maxLength: FieldLengths.INSERTION.toString()
            }),
            insertionAllowed: this.contentService.get('personal.details.insertion.error.insertionAllowed')
        };
        this.lastNameErrorMessages = {
            required: this.contentService.get('personal.details.lastName.error.required'),
            incorrect: this.contentService.get('personal.details.lastName.error.incorrect')
        };
    }
}
